import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import {
  useCreateTestimonialMutation,
  useUpdateTestimonialMutation,
} from "../api/testimonialApi";
import { Testimonial } from "../types/testimonal";

interface Props {
  open: boolean;
  setModalOpen: (open: boolean) => void;
  onSuccess?: () => void;
  testimonial?: Testimonial | null;
}

const AddEditTestimonial: React.FC<Props> = ({
  open,
  setModalOpen,
  onSuccess,
  testimonial,
}) => {
  const [formData, setFormData] = useState<{
    name: string;
    position: string;
    message: string;
    rating: string;
    status: "approved" | "pending" | "rejected";
    photo: File | null;
    existingPhoto?: string;
  }>({
    name: "",
    position: "",
    message: "",
    rating: "",
    status: "pending",
    photo: null,
    existingPhoto: undefined,
  });

  const [addTestimonial] = useCreateTestimonialMutation();
  const [updateTestimonial] = useUpdateTestimonialMutation();
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (testimonial) {
      setFormData({
        name: testimonial.name,
        position: testimonial.position,
        message: testimonial.message,
        rating: testimonial.rating,
        status: testimonial.status,
        photo: null,
        existingPhoto: testimonial.photo,
      });
    } else {
      setFormData({
        name: "",
        position: "",
        message: "",
        rating: "",
        status: "pending",
        photo: null,
        existingPhoto: undefined,
      });
    }
  }, [testimonial, open]);

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setFormData((prev) => ({ ...prev, photo: file }));
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    const fd = new FormData();
    fd.append("name", formData.name);
    fd.append("position", formData.position);
    fd.append("message", formData.message);
    fd.append("rating", formData.rating);
    fd.append("status", formData.status);
    if (formData.photo) {
      fd.append("photo", formData.photo);
    }

    try {
      if (testimonial?.id) {
        await updateTestimonial({ id: testimonial.id, body: fd }).unwrap();
      } else {
        await addTestimonial({ body: fd }).unwrap();
      }
      setModalOpen(false);
      onSuccess?.();
      setSubmissionError(null);
    } catch (err) {
      console.error("Error saving testimonial:", err);
      setSubmissionError("Failed to save testimonial. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => setModalOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>{testimonial ? "Edit Testimonial" : "Add Testimonial"}</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          margin="dense"
          label="Name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
        />
        <TextField
          fullWidth
          margin="dense"
          label="Position"
          value={formData.position}
          onChange={(e) => setFormData({ ...formData, position: e.target.value })}
        />
        <TextField
          fullWidth
          margin="dense"
          type="number"
          inputProps={{ min: 0, max: 5, step: 1}}
          label="Rating"
          value={formData.rating}
          onChange={(e) => setFormData({ ...formData, rating: e.target.value })}
        />
        <TextField
          fullWidth
          margin="dense"
          label="Status"
          select
          value={formData.status}
          onChange={(e) =>
            setFormData({ ...formData, status: e.target.value as "approved" | "pending" | "rejected" })
          }
        >
          <MenuItem value="approved">Approved</MenuItem>
          <MenuItem value="pending">Pending</MenuItem>
          <MenuItem value="rejected">Rejected</MenuItem>
        </TextField>
        <TextField
          fullWidth
          margin="dense"
          label="Message"
          multiline
          rows={4}
          value={formData.message}
          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
        />

        <Box sx={{ mt: 2 }}>
          <Button variant="outlined" component="label">
            Upload Photo
            <input hidden accept="image/*" type="file" onChange={handlePhotoChange} />
          </Button>

          {formData.existingPhoto && !formData.photo && (
            <Box mt={1}>
              <img src={formData.existingPhoto} alt="photo" width={100} />
            </Box>
          )}
          {formData.photo && (
            <Box mt={1}>
              <img src={URL.createObjectURL(formData.photo)} alt="preview" width={100} />
            </Box>
          )}
        </Box>

        {submissionError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {submissionError}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setModalOpen(false)} disabled={isLoading}>
          Cancel
        </Button>
        <Button 
          variant="contained" 
          onClick={handleSubmit}
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {isLoading ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddEditTestimonial;

